/**
 * The three ways a proposal can look, designed in Figma
 * ("Abstract Creative · Proposal templates", file eKFP0bsT3rOy7KEIx5uEUv).
 *
 * The look changes presentation only: the same sections, prices and accept
 * flow render in every look. It is stored on the proposal as `content.look`,
 * so the public page reads it through get_proposal_by_token with no SQL change.
 *
 * All three are white, black and red, Public Sans, square corners, no rule
 * lines; big numbers are drawn as outlines; grouping is done with soft panels.
 */

export const DEFAULT_LOOK = 'editorial'

export const LOOKS = {
  editorial: {
    id: 'editorial',
    label: 'Editorial',
    description: 'White page, big black type, red accents. Calm and confident.',
    cover: { bg: '#FFFFFF', ink: '#1A1A1A', accentBar: true, logo: 'color' },
    panel: '#F5F4F2',
    sectionNumber: 'kicker',   // small red number beside the section label
    header: 'none',
  },
  swiss: {
    id: 'swiss',
    label: 'Swiss grid',
    description: 'Black section bars, oversized outlined numbers, strict grid.',
    cover: { bg: '#1A1A1A', ink: '#FFFFFF', accentBar: true, logo: 'white', redSquare: true },
    panel: '#F5F4F2',
    sectionNumber: 'giant-left', // big outlined number beside the headline
    header: 'bar',               // black bar across the top of each page
  },
  bold: {
    id: 'bold',
    label: 'Bold red',
    description: 'Red cover, giant outlined red numbers, soft red panels.',
    cover: { bg: '#EF4237', ink: '#FFFFFF', accentBar: false, logo: 'white', inkBand: true },
    panel: '#FDEFED',
    sectionNumber: 'giant-right', // giant outlined red number bleeding off the top right
    header: 'none',
    redInvestment: true,          // price sits in a full red block
  },
}

export const COLORS = {
  red: '#EF4237',
  ink: '#1A1A1A',
  muted: '#6B6B69',
  white: '#FFFFFF',
}

export const lookIds = Object.keys(LOOKS)

/** The look a proposal uses; anything unknown falls back to Editorial. */
export function lookOf(content) {
  const id = content && typeof content === 'object' ? content.look : null
  return LOOKS[id] ? id : DEFAULT_LOOK
}

/**
 * Copy every proposal carries unless the studio overrides it. Written in the
 * studio's voice: plain, first person from Efren, no em dashes.
 */
export const STANDARD_COPY = {
  whyUs: 'You work with the person doing the work. A small studio, senior attention on every decision, a fixed price, and a clear yes before each stage starts.',
  weNeed: [
    ['One decision-maker', 'A named person who approves each stage and gathers the team’s notes.'],
    ['Feedback within 5 business days', 'Faster answers keep the launch date.'],
    ['Access by kickoff', 'Logins, brand files, analytics and research, shared in the portal.'],
    ['Interviews in week one', 'Up to 6 people, 30 to 45 minutes each.'],
    ['Time at key meetings', 'Kickoff, readout, design presentation and handover.'],
  ],
  youGet: [
    ['Replies within one business day', 'Every message gets an answer the next working day.'],
    ['A weekly update', 'Every Friday: done, next, and what we need from you.'],
    ['Approval at every stage', 'Nothing moves on without your written yes.'],
    ['Everything in one place', 'Files, feedback, approvals and invoices in your portal.'],
    ['Senior attention', 'Efren leads the work and presents every stage.'],
  ],
  revisionRounds: 'A round is one consolidated set of feedback on a deliverable. Each deliverable includes two. New directions are quoted as a change order first.',
  terms: [
    ['Payment', '50% to start, 25% when strategy is approved, 25% on delivery. Due within 15 days, by card or bank transfer.'],
    ['Late payment', 'More than 30 days overdue pauses the work and adds 1.5% interest a month.'],
    ['Revisions', 'Two rounds per deliverable. Extra work is $95 an hour, agreed in writing first.'],
    ['Changes to scope', 'Written up as a change order with price and timing before work starts.'],
    ['Timeline', 'Assumes feedback within 5 business days. Reviews over 10 days pause the project.'],
    ['Ownership', 'On final payment you own all deliverables. Working files and unused concepts stay with the studio.'],
    ['Third-party costs', 'Fonts, stock, printing and travel are licensed in your name and billed at cost, with approval.'],
    ['Portfolio', 'We may show the finished work after launch, unless you ask us not to in writing.'],
    ['Confidentiality', 'Anything you share stays private and is used only for this project.'],
    ['Cancelling', '14 days’ written notice. Work to date is billed; the first payment is non-refundable.'],
    ['Liability', 'Limited to the fees paid. Governed by the laws of the State of Texas.'],
    ['This proposal', 'Prices and dates hold for 30 days from the cover date.'],
  ],
  included: [
    ['Your own project portal', 'Updates, files, approvals and invoices in one place.'],
    ['One point of contact', 'Efren leads the work from kickoff to handover.'],
    ['You own all of it', 'Every final file is yours once the project is paid.'],
  ],
  faq: [
    ['Can we start sooner?', 'Yes, if kickoff is booked this week.'],
    ['Need more rounds?', 'Extra work is $95 an hour, agreed in writing first.'],
    ['Who owns the work?', 'You do, in full, once the final invoice is paid.'],
  ],
  nextSteps: [
    ['Accept online', 'One click on this proposal. It takes a minute.'],
    ['Pay the first payment', 'By card or bank transfer, on the same page.'],
    ['Kickoff', 'We meet within 3 business days and start the same week.'],
  ],
}
